import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllCouriers,
  selectCouriersState,
  CourierItem,
} from '../../redux-toolkit/slice/onboardingSlice';
import { AppDispatch } from '../../redux-toolkit/store';
import { Avatar, Divider, Flex, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import CustomTitle from '../../components/CustomTitle';
import CustomParagraph from '../../components/CustomParagraph';
import CustomInput from '../../components/CustomInput';
import { getProviderImagePath } from '../../common/utils/imageUtils';
import { compareGroundTransportProviders } from '../../common/utils/transportProviderGroundSort';
import theme from '@/assets/theme';
import type { VendorConnectionData } from '@/apis/vendorConnections';
import './TransportProviderForm.css';

interface TransportProviderFormProps {
  token: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  setSelectedProvider: (provider: CourierItem) => void;
  setModalOpen: (open: boolean) => void;
  /** Quoting and booking vendors the user may connect (Freightos, Cargo One).
   * Empty or omitted hides the "Quoting and Booking" category. */
  quotingVendors?: VendorConnectionData[];
  onSelectQuotingVendor?: (vendor: VendorConnectionData) => void;
}

/** What one option in the list needs: an id, a name (also the logo lookup) and
 * what happens when it is picked. */
interface ProviderOption {
  id: string;
  title: string;
  onSelect: () => void;
}

/**
 * Form component for selecting and adding transport providers/carriers
 *
 * @param token - Authentication token
 * @param searchQuery - Current search filter query
 * @param setSearchQuery - Callback to update search query
 * @param setSelectedProvider - Callback when a provider is selected
 * @param setModalOpen - Callback to open the provider details modal
 * @param quotingVendors - Vendors for the "Quoting and Booking" category
 * @param onSelectQuotingVendor - Callback when one of those vendors is picked
 */
const TransportProviderForm: React.FC<TransportProviderFormProps> = ({
  token,
  searchQuery,
  setSearchQuery,
  setSelectedProvider,
  setModalOpen,
  quotingVendors = [],
  onSelectQuotingVendor,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { courierList, status } = useSelector(selectCouriersState);

  useEffect(() => {
    void Promise.resolve(dispatch(fetchAllCouriers(token))).catch(
      (): undefined => undefined
    );
  }, [dispatch, token]);

  const OCEAN_TITLES = new Set([
    'CMA CGM',
    'MSC',
    'Hapag Lloyd',
    'ONE',
    'HMM',
    'Maersk',
    'ICL',
  ]);

  const AIRLINE_TITLES = new Set([
    'American Airlines',
    'United Airlines',
    'Delta Airlines',
    'Southwest Airlines',
  ]);

  const filteredProviders: CourierItem[] = courierList.filter((provider) =>
    provider?.title?.toLowerCase().includes(searchQuery?.toLowerCase())
  );

  const groundProviders = filteredProviders
    .filter(
      (p) =>
        p.instructions !== 'awb_basic' &&
        !AIRLINE_TITLES.has(p.title) &&
        p.title?.toLowerCase() !== 'other'
    )
    .sort(compareGroundTransportProviders);
  const airProviders = filteredProviders
    .filter(
      (p) =>
        (p.instructions === 'awb_basic' && !OCEAN_TITLES.has(p.title)) ||
        AIRLINE_TITLES.has(p.title)
    )
    .sort((a, b) => {
      if (a.title === 'All Airlines') return 1;
      if (b.title === 'All Airlines') return -1;
      return 0;
    });
  const oceanProviders = filteredProviders.filter(
    (p) => p.instructions === 'awb_basic' && OCEAN_TITLES.has(p.title)
  );
  const otherProvider = filteredProviders.filter(
    (p) => p.title?.toLowerCase() === 'other'
  );

  const toOption = (provider: CourierItem): ProviderOption => ({
    id: provider.id,
    title: provider.title,
    onSelect: () => {
      setSelectedProvider(provider);
      setModalOpen(true);
    },
  });

  const quotingOptions: ProviderOption[] = quotingVendors
    .filter((vendor) =>
      vendor.display_name?.toLowerCase().includes(searchQuery?.toLowerCase())
    )
    .map((vendor) => ({
      id: vendor.vendor,
      title: vendor.display_name,
      onSelect: () => onSelectQuotingVendor?.(vendor),
    }));

  const renderProviderItem = (provider: ProviderOption) => {
    const imagePath = getProviderImagePath(provider.title, 'transportProvider');
    return (
      <Flex
        key={provider.id}
        align='center'
        justify='space-between'
        style={{
          flex: 1,
          padding: '6px 0px',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
        }}
        className='custom-select'
        onClick={provider.onSelect}
      >
        <Flex align='center' gap={10} style={{ marginLeft: '6px' }}>
          <Avatar
            size={34}
            src={
              <img
                src={imagePath}
                alt='avatar'
                className='transport-provider-form__avatar-img'
              />
            }
            style={{
              backgroundColor: 'var(--surface-container)',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#555',
            }}
          >
            {provider.title.charAt(0)}
          </Avatar>
          <CustomParagraph
            marginBottom={'0px'}
            text={provider.title}
            fontSize={12}
            fontWeight={600}
          />
        </Flex>
      </Flex>
    );
  };

  const renderGroup = (label: string, providers: ProviderOption[]) => {
    if (providers.length === 0) return null;
    const groupRows: ProviderOption[][] = [];
    for (let i = 0; i < providers.length; i += 2)
      groupRows.push(providers.slice(i, i + 2));
    return (
      <>
        <div className='transport-provider-form__group-label'>{label}</div>
        {groupRows.map((row) => (
          <React.Fragment key={row.map((p) => p.id).join('-')}>
            <Flex justify='space-between' gap={12}>
              {row.map((p) => renderProviderItem(p))}
              {row.length === 1 && (
                <div className='transport-provider-form__row-spacer' />
              )}
            </Flex>
            {row !== groupRows[groupRows.length - 1] && (
              <Divider style={{ margin: '6px 0' }} />
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Category order in the list; a category with no options is not shown.
  const groups: Array<[string, ProviderOption[]]> = [
    ['Ground', groundProviders.map(toOption)],
    ['Air', airProviders.map(toOption)],
    ['Ocean', oceanProviders.map(toOption)],
    ['Quoting and Booking', quotingOptions],
    ['Other', otherProvider.map(toOption)],
  ];

  const labeltext: string =
    theme.themeType === 2 ? 'carrier' : 'transport provider';
  return (
    <div className='transport-provider-form'>
      <>
        <div className='drawer-title-header'>
          <CustomTitle
            text={`Add ${labeltext}`}
            fontSize={20}
            fontWeight='700'
            lineHeight='32px'
            color='var(--text-primary)'
            marginBottom={'10px'}
          />
          <CustomInput
            className='custom-input form-input-style'
            width={'98%'}
            bgColor='var(--light-gray)'
            height='36px'
            textColor='var(--text-primary)'
            borderRadius='20px'
            border='none'
            placeholderColor='var(--text-secondary)'
            placeholder={`Search for a ${labeltext}...`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            prefix={
              <SearchOutlined
                style={{
                  color: 'var(--text-primary)',
                  fontSize: '12px',
                  marginRight: '8px',
                }}
              />
            }
          />
        </div>

        <div className='custom-scrollbar transport-provider-form__scroll'>
          {status === 'loading' ? (
            <Flex justify='center' align='center' style={{ height: '100%' }}>
              {' '}
              <Spin />
            </Flex>
          ) : courierList.length > 0 || quotingOptions.length > 0 ? (
            <>
              {groups
                .filter(([, options]) => options.length > 0)
                .map(([label, options], index) => (
                  <React.Fragment key={label}>
                    {index > 0 && (
                      <div className='transport-provider-form__group-gap' />
                    )}
                    {renderGroup(label, options)}
                  </React.Fragment>
                ))}
            </>
          ) : (
            <Flex justify='center' align='center' style={{ height: '100%' }}>
              <CustomParagraph
                text='No transport providers available'
                fontSize={14}
              />
            </Flex>
          )}
        </div>
      </>
    </div>
  );
};

export default TransportProviderForm;
